/**
 * Oportunidades de economia como "ofertas" contratáveis (out/2026, conceito do
 * Figma — página de Oportunidades, só admin enquanto o Bruno valida).
 *
 * Oportunidade = o que dá economia ou payback: as do motor
 * (`GET /analysis/.../opportunities` — demanda, modalidade, Tarifa Branca,
 * mercado livre, energia por assinatura, migração de grupo). A contestação das
 * cobranças a mais saiu daqui em 27/out/2026: virou o quadro de auditoria da
 * página, fatura a fatura (`audit-contestation`). Os serviços sem economia calculável (limpeza, visita, vencimento…) ficam em
 * Serviços Adicionais.
 *
 * Também é a fonte única do pedido que cada oportunidade gera
 * (`opportunityRequest`), usada pela lista e pela análise detalhada da área
 * interna — antes a regra morava só no componente da análise.
 */
import {
  OpportunitiesResult,
  OpportunityItem,
} from 'services/analysis.service';
import {
  ServiceCatalogItem,
  ServiceRequestItem,
  ServiceRequestPayload,
  ServiceType,
} from 'services/service_requests.service';
import {
  RateioGeneratorView,
  RateioOptimization,
} from 'services/credit.service';
import { catalogPriceLabel } from './serviceCatalog';
import { format } from './format';

const fmtBRL = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const asNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

const fmtKw = (v: unknown) => `${format.demand(asNumber(v) ?? 0)} kW`;

/**
 * Serviço de cada oportunidade: demanda e modalidade (inclui a Tarifa Branca)
 * têm serviço próprio com payload estruturado; as demais viram "Estudo de
 * economia" com o resumo da análise.
 */
export const opportunityServiceType = (opp: OpportunityItem): ServiceType =>
  opp.id === 'demand_optimization'
    ? 'alteracao_demanda'
    : opp.id === 'modality_migration' || opp.id === 'tariff_branca'
    ? 'alteracao_modalidade'
    : 'estudo_economia';

/**
 * Resumo da oportunidade que vai no pedido de "Estudo de economia" (out/2026 —
 * antes era a mensagem pré-preenchida de um link de WhatsApp para o número
 * placeholder 5500000000000, que não levava a lugar nenhum).
 */
export const hireMessage = (ucLabel: string, opp: OpportunityItem): string => {
  const saving = `${fmtBRL(opp.monthlySavings)}/mês (${fmtBRL(
    opp.annualSavings
  )}/ano)`;
  switch (opp.id) {
    case 'demand_optimization':
      return `A análise do EnergyPass identificou economia de ${saving} ajustando a demanda contratada da unidade "${ucLabel}" de ${fmtKw(opp.details.contractedKW)} para ${fmtKw(opp.details.suggestedKW)}.`;
    case 'modality_migration':
      return `A análise do EnergyPass identificou economia de ${saving} migrando a unidade "${ucLabel}" da modalidade ${opp.details.from} para ${opp.details.to}.`;
    case 'tariff_branca':
      return `A análise do EnergyPass identificou economia de ${saving} migrando a unidade "${ucLabel}" para a Tarifa Branca.`;
    case 'free_market':
      return `A análise do EnergyPass identificou economia de ${saving} migrando a unidade "${ucLabel}" para o mercado livre de energia.`;
    case 'energy_subscription': {
      const pct =
        typeof opp.details.discountPct === 'number'
          ? ` (desconto de ${Math.round(
              opp.details.discountPct * 100
            )}% na energia)`
          : '';
      return `A análise do EnergyPass identificou economia de ${saving} aderindo à energia por assinatura${pct} para a unidade "${ucLabel}".`;
    }
    case 'group_migration':
      return `A análise do EnergyPass identificou economia de ${saving} migrando a unidade "${ucLabel}" de ${opp.details.from} para ${opp.details.to} (grupo tarifário).`;
    case 'seasonal_migration':
      return `A análise do EnergyPass identificou economia de ${saving} pedindo à distribuidora o enquadramento da unidade "${ucLabel}" como rural ou sazonal (demanda contratada sugerida: ${fmtKw(opp.details.suggestedKW)}).`;
    default:
      return `Quero saber mais sobre a oportunidade "${opp.title}" da unidade "${ucLabel}" (economia de ${saving}).`;
  }
};

/**
 * Pedido que a oportunidade gera, já com os dados da análise (demanda sugerida
 * / modalidade destino) — o cliente não redigita. O backend valida de novo
 * (`ServiceRequestsService.validatePayload`).
 */
export const opportunityRequest = (
  ucLabel: string,
  opp: OpportunityItem
):
  | { serviceType: ServiceType; payload: ServiceRequestPayload }
  | { error: string } => {
  const serviceType = opportunityServiceType(opp);
  const payload: ServiceRequestPayload = {};

  if (serviceType === 'estudo_economia') {
    payload.reason = hireMessage(ucLabel, opp);
    if (opp.monthlySavings > 0) {
      payload.estimatedMonthlySavings =
        Math.round(opp.monthlySavings * 100) / 100;
    }
  } else if (opp.id === 'tariff_branca') {
    payload.requestedModality = 'branca';
    payload.currentModality = 'convencional';
  } else if (serviceType === 'alteracao_demanda') {
    const suggested = asNumber(opp.details.suggestedKW);
    const current = asNumber(opp.details.contractedKW);

    if (!suggested) {
      return { error: 'Não foi possível identificar a demanda sugerida.' };
    }

    payload.requestedDemandKw = suggested;
    if (current) payload.currentDemandKw = current;
  } else {
    const to = String(opp.details.to ?? '').toLowerCase();

    if (!['verde', 'azul', 'branca', 'convencional'].includes(to)) {
      return { error: 'Não foi possível identificar a modalidade sugerida.' };
    }

    payload.requestedModality = to as NonNullable<
      ServiceRequestPayload['requestedModality']
    >;
    if (typeof opp.details.from === 'string') {
      payload.currentModality = opp.details.from;
    }
  }

  return { serviceType, payload };
};

/**
 * Trecho do resumo (`hireMessage`) que identifica cada "Estudo de economia" —
 * vários cards usam o mesmo tipo de serviço e o payload só guarda o texto.
 */
const STUDY_MATCH: Partial<Record<OpportunityItem['id'], string>> = {
  free_market: 'mercado livre',
  energy_subscription: 'energia por assinatura',
  group_migration: 'grupo tarifário',
  seasonal_migration: 'rural ou sazonal',
};

/** O pedido corresponde a esta oportunidade? (cards que dividem o tipo). */
export const opportunityMatchesRequest = (
  oppId: OpportunityOffer['id'],
  request: ServiceRequestItem
): boolean => {
  if (oppId === 'tariff_branca') {
    return request.payload?.requestedModality === 'branca';
  }
  if (oppId === 'modality_migration') {
    return request.payload?.requestedModality !== 'branca';
  }
  const hint = oppId === 'rateio' ? undefined : STUDY_MATCH[oppId];
  return hint ? !!request.payload?.reason?.includes(hint) : true;
};

/**
 * Observações que a janela de contratação mostra (prazos e regras da REN ANEEL
 * 1000/2021) — pedido do Bruno (out/2026): o card mostra só economia e custo;
 * as condições aparecem quando o cliente decide contratar.
 */
export const opportunityHireNotes = (opp?: OpportunityItem): string[] => {
  if (!opp) return [];
  const isRural = !!opp.details.ruralRule;
  const contracted = asNumber(opp.details.contractedKW);
  const suggested = asNumber(opp.details.suggestedKW);
  const reduces =
    contracted !== undefined &&
    suggested !== undefined &&
    suggested < contracted;

  if (opp.id === 'demand_optimization') {
    const notes: string[] = [];
    if (reduces) {
      notes.push(
        'A redução da demanda contratada precisa ser pedida com 90 dias de antecedência (subgrupos A4 e AS; 180 dias nos demais) e só pode ser feita uma vez a cada 12 meses (art. 155). A economia começa depois desse prazo.'
      );
    } else {
      notes.push(
        'O aumento da demanda é analisado pela distribuidora e pode exigir obra na conexão (art. 154).'
      );
    }
    notes.push(
      'A demanda contratada mínima é de 30 kW em pelo menos um posto tarifário (art. 148).'
    );
    if (isRural) {
      notes.push(
        'Unidade rural ou sazonal: se em 12 meses não houver ao menos 3 meses com a demanda faturada igual ou acima da contratada, a distribuidora cobra a demanda complementar (art. 300). A demanda sugerida já leva isso em conta.'
      );
    }
    notes.push(
      'A estimativa usa os últimos 12 meses de faturas; a distribuidora apura a demanda complementar a partir do início do contrato.'
    );
    return notes;
  }

  if (opp.id === 'seasonal_migration') {
    return [
      'A distribuidora tem até 5 dias úteis para analisar o pedido de sazonalidade, ou 10 dias úteis se precisar de visita técnica (art. 296).',
      'A sazonalidade é reavaliada a cada 12 ciclos; se os requisitos deixarem de ser atendidos, o reconhecimento é suspenso e um novo pedido só pode ser feito depois de mais 12 ciclos (arts. 297 e 298).',
      'Para a classe rural, a distribuidora pode pedir comprovação da atividade agropecuária (registro de produtor rural ou documento equivalente — art. 184).',
      'Com o enquadramento, a demanda complementar é cobrada a cada 12 meses quando não houver 3 meses com a demanda faturada igual ou acima da contratada (art. 300). Rural e sazonal podem contratar a demanda por cronograma mensal (art. 152).',
      ...(reduces
        ? [
            'A redução da demanda contratada sugerida segue o prazo de 90 dias (A4/AS) e só pode ser feita uma vez a cada 12 meses (art. 155).',
          ]
        : []),
    ];
  }

  return [];
};

/** Meses para o custo do serviço se pagar; null sem custo ou sem economia. */
export const paybackMonths = (
  cost: number | null | undefined,
  monthlySavings: number | null | undefined
): number | null => {
  if (cost == null || !monthlySavings || monthlySavings <= 0) return null;
  return cost / monthlySavings;
};

export const formatPayback = (months: number | null): string => {
  if (months == null) return '—';
  if (months < 1) return 'menos de 1 mês';
  if (months < 24) {
    const m = Math.ceil(months);
    return `${m} ${m === 1 ? 'mês' : 'meses'}`;
  }
  return `${(months / 12).toLocaleString('pt-BR', {
    maximumFractionDigits: 1,
  })} anos`;
};

export interface OpportunityOffer {
  /**
   * Id da oportunidade do motor ou `rateio`
   * (otimizador do rateio de créditos — `utils/rateioOffer`).
   */
  id: OpportunityItem['id'] | 'rateio';
  title: string;
  description: string;
  serviceType: ServiceType;
  /** Economia mensal (R$). */
  monthlySavings: number | null;
  cost: number | null;
  costLabel: string;
  paybackMonths: number | null;
  /** Oportunidade do motor — os detalhes da análise vão na janela. */
  opportunity?: OpportunityItem;
  /** Rateio: "Contratar" grava uma alteração por UG (`apply`), não um pedido. */
  rateio?: { analysis: RateioOptimization; generators: RateioGeneratorView[] };
  request:
    | { serviceType: ServiceType; payload: ServiceRequestPayload }
    | { error: string };
}

/**
 * Ofertas de um imóvel: oportunidades do motor recomendadas (economia > 0),
 * maior economia mensal primeiro.
 */
export const buildOpportunityOffers = (
  ucLabel: string,
  result: OpportunitiesResult | undefined,
  catalog: ServiceCatalogItem[]
): OpportunityOffer[] => {
  const catalogItem = (type: ServiceType) =>
    catalog.find((c) => c.serviceType === type);

  const fromEngine = (result?.opportunities ?? [])
    .filter((opp) => opp.eligible && opp.recommended && opp.monthlySavings > 0)
    .map((opp): OpportunityOffer => {
      const serviceType = opportunityServiceType(opp);
      const item = catalogItem(serviceType);
      const cost = item?.price ?? null;
      return {
        id: opp.id,
        title: opp.title,
        description: opp.description,
        serviceType,
        monthlySavings: opp.monthlySavings,
        cost,
        costLabel: catalogPriceLabel(item) || 'Sob consulta',
        paybackMonths: paybackMonths(cost, opp.monthlySavings),
        opportunity: opp,
        request: opportunityRequest(ucLabel, opp),
      };
    })
    .sort((a, b) => (b.monthlySavings ?? 0) - (a.monthlySavings ?? 0));

  return fromEngine;
};

/** Economia mensal somada das ofertas (a contestação é valor único, fica fora). */
export const offersMonthlySavings = (offers: OpportunityOffer[]): number =>
  offers.reduce((sum, o) => sum + (o.monthlySavings ?? 0), 0);
